import * as React from 'react';

import { DatePicker as MatDatePicker, DatePickerProps as MatDatePickerProps, pickersDayClasses } from '@mui/x-date-pickers';

import { iconButtonClasses, inputAdornmentClasses, SxProps, TextField, Theme } from '@mui/material';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { calendarPickerClasses, LocalizationProvider } from '@mui/x-date-pickers';
import moment from 'moment';
import { archerConstants } from '../../utils';

export type DatePickValue = object | string | number | Date | null | undefined;
type MarginType = 'dense' | 'normal' | 'none';

export type DatepickerProps = {
  disabled?: boolean;
  disableFutureDates?: boolean;
  // to disable specific dates conditionally in the Date Picker
  shouldDisableDate?: (date: unknown) => boolean;
  disableToolbar?: boolean;
  disablePastDates?: boolean;
  label?: string;
  locale?: string;
  margin?: MarginType;
  menuRef?: React.Ref<HTMLDivElement>;
  onChange: (date: unknown) => void;
  translate: any;
  value: DatePickValue;
  fullWidth?: boolean;
  error?: boolean;
  helperText?: string;
  readOnly?: boolean;
  required?: boolean;
  sxTextField?: SxProps<Theme>;
  sxPopper?: SxProps<Theme>;
};

const styles: Record<string, SxProps<Theme>> = {
  datePickerRoot: {
    maxHeight: '3rem',
    '& > div': {
      maxHeight: '3rem'
    },
    '& > input': {
      maxHeight: '3rem'
    },
    [`& .${inputAdornmentClasses['positionEnd']}`]: {
      paddingRight: '3px'
    }
  },
  datePickerDialogRoot: {
    [`& .${calendarPickerClasses['root']}`]: {
      [`& .MuiPickersArrowSwitcher-root .MuiPickersArrowSwitcher-button`]: {
        color: 'text.secondary'
      },
      [`& [role="presentation"] .${iconButtonClasses['root']}`]: {
        color: 'text.secondary'
      }
    },
    [`& .${pickersDayClasses['selected']}`]: {
      color: archerConstants.archerBackgroundPaper,
      backgroundColor: 'primary.main',
      '&:hover, &:focus, &:active': {
        backgroundColor: `${archerConstants.archerPrimaryButtonHover} !important`
      }
    }
  }
};

const ariaLabelPropertyName = 'aria-label';

const Datepicker = ({
  disabled,
  disableFutureDates,
  shouldDisableDate,
  disableToolbar,
  disablePastDates,
  label,
  locale,
  onChange,
  value,
  fullWidth,
  margin,
  menuRef,
  error,
  helperText,
  readOnly,
  required,
  sxTextField,
  sxPopper
}: DatepickerProps) => {
  moment.locale(locale);
  const { datePickerRoot, datePickerDialogRoot } = styles;
  const format = moment().localeData().longDateFormat('L');

  const baseProps: MatDatePickerProps<any, any> = {
    // maxDateMessage: maxDateMessage,
    // minDateMessage: minDateMessage,
    OpenPickerButtonProps: {
      [ariaLabelPropertyName]: label,
      color: 'primary'
    },

    renderInput: (params: any) => (
      <TextField
        readOnly={readOnly}
        variant="filled"
        sx={[datePickerRoot, sxTextField] as SxProps<Theme>}
        fullWidth={fullWidth}
        margin={margin}
        required={required}
        error={error}
        helperText={helperText}
        {...{ ...(params as any), [ariaLabelPropertyName]: label }}
      />
    ),
    leftArrowButtonText: 'left',
    rightArrowButtonText: 'right',
    disableFuture: disableFutureDates,
    disablePast: disablePastDates,
    shouldDisableDate: shouldDisableDate,
    // invalidDateMessage: invalidDate,
    disabled,
    showToolbar: !disableToolbar,
    inputFormat: format,
    label,
    onChange,
    value,
    readOnly
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <MatDatePicker
        {...{
          ...baseProps,
          PopperProps: {
            sx: [datePickerDialogRoot, sxPopper] as SxProps<Theme>,
            ref: menuRef
          }
        }}
      />
    </LocalizationProvider>
  );
};

Datepicker.defaultProps = {
  disabled: false,
  disablePastDates: false,
  disableFutureDates: false,
  shouldDisableDate: () => false,
  locale: 'en-US',
  margin: 'normal',
  readOnly: false,
  required: false
} as Partial<DatepickerProps>;

Datepicker.displayName = 'Datepicker';

export { Datepicker };
